import {IFloatingToolbarGroup} from '@techsee/techsee-ui-common/lib/floating-toolbar/contracts';
import {IToolbarOptionsController, ToolbarOptionsController} from './ts-toolbar-options.controller';
import './ts-floating-toolbar.style.scss';
import {Boundaries} from '@techsee/techsee-ui-common/lib/draggble-panel/contracts';
import {DeviceCategory} from '@techsee/techsee-common/lib/constants/utils.constant';
import {computed} from 'mobx';

export interface IFloatingToolbarServices {
    tsCanvasAnnotate: any;
    environmentService: any;
    displayTabletAsDesktop: boolean;
    domUtilsService: any;
    localizationService: any;
    speedtestService: any;
    roomService: any;
    eventsLogService: any;
}

export interface ITsFloatingToolbarController {
    readonly boundaries: Boundaries;
    readonly offsetBoundaries: Boundaries;
    readonly initialized: boolean;
    readonly toolbarOptions: IFloatingToolbarGroup[];

    toolbarOptionsController: IToolbarOptionsController;
    shouldDisplay: boolean;
}

export class TsFloatingToolbarController implements ITsFloatingToolbarController {
    private _element?: HTMLElement;

    private _tsCanvasAnnotate: any;

    private _initialized: boolean = false;

    private _prevToolbarHeight: number = 0;

    private _toolbarOptionsController: IToolbarOptionsController;

    private _deviceCategory: DeviceCategory = DeviceCategory.desktop;

    private isIpadTouchSafari(): boolean {
        const userAgent = navigator.userAgent || '';
        const vendor = navigator.vendor || '';
        const isAppleWebKitSafari =
            /Safari/i.test(userAgent) && /Apple/i.test(vendor) && !/CriOS|FxiOS|EdgiOS/i.test(userAgent);
        const hasTouch = (navigator as Navigator & {maxTouchPoints?: number}).maxTouchPoints
            ? (navigator as Navigator & {maxTouchPoints?: number}).maxTouchPoints! > 1
            : false;
        const isIpadPlatform = /iPad/i.test(userAgent) || (navigator.platform === 'MacIntel' && hasTouch);
        return isAppleWebKitSafari && isIpadPlatform;
    }

    constructor(
        private dashboardState: any,
        private accountData: any,
        private services: IFloatingToolbarServices
    ) {
        this._tsCanvasAnnotate = services.tsCanvasAnnotate;

        const shouldForceTabletBehavior = !!services.environmentService.shouldForceTabletBehavior?.(
            services.displayTabletAsDesktop
        );
        const resolvedDeviceCategory = shouldForceTabletBehavior
            ? DeviceCategory.tablet
            : services.environmentService.deviceCategory(services.displayTabletAsDesktop);

        this._deviceCategory = resolvedDeviceCategory || DeviceCategory.desktop;
        this._initialized = false;
        this._toolbarOptionsController = new ToolbarOptionsController(this.accountData, this.dashboardState, services);
    }

    init(element: HTMLElement) {
        this._element = element;

        if (!this._initialized) {
            const toolbarMargin = 30;

            const mainContent = document.querySelector('.dashboard-main-content-container');
            const toolbarEl = this._element;

            if (!mainContent || !toolbarEl) {
                return;
            }

            const mainCoords = mainContent.getBoundingClientRect();
            const toolbarHeight = (toolbarEl as HTMLElement).offsetHeight;
            const toolbarWidth = (toolbarEl as HTMLElement).offsetWidth;

            if (mainCoords.right > 0 && mainCoords.bottom > 0 && toolbarWidth && toolbarHeight) {
                this._initialized = true;
            } else {
                return;
            }

            this.dashboardState.floatingToolbarState.setToolbarPos(
                mainCoords.right - toolbarWidth - toolbarMargin,
                mainCoords.bottom - toolbarHeight - toolbarMargin
            );

            this._prevToolbarHeight = toolbarHeight;
        }
    }

    @computed
    get shouldDisplay(): boolean {
        return this.dashboardState.displayFloatingToolbar && !this.dashboardState.endedTheMeeting;
    }

    get initialized() {
        return this._initialized;
    }

    @computed
    get toolbarPos() {
        return this.dashboardState.floatingToolbarState.toolbarPos;
    }

    get deviceCategory() {
        return this._deviceCategory;
    }

    // Getting top, bottom, left and right edges to limit moving toolbar across the window
    get boundaries(): Boundaries {
        return {
            left: 0,
            top: 0,
            right: window.innerWidth,
            bottom: window.innerHeight
        };
    }

    get offsetBoundaries(): Boundaries {
        const newLeftBar = document.querySelector('.app-left-bar') as HTMLElement;
        const newTopBar = document.querySelector('.app-top-bar') as HTMLElement;

        return {
            left: newLeftBar?.offsetWidth || 0,
            top: newTopBar?.offsetHeight || 0,
            right: 0,
            bottom: 0
        };
    }

    get toolbarOptionsController() {
        return this._toolbarOptionsController;
    }

    @computed
    get toolbarOptions() {
        return this._toolbarOptionsController.toolbarOptions;
    }
}
